<template>
  <div class="versions-page">
    <div class="container mt-4 versions-container">
      <div class="header-section mb-4">
        <!-- Titre principal -->
        <div class="header-title-section mb-3">
          <h2 class="header-title">Historique des versions</h2>
        </div>
        
        <!-- Informations de version et bouton administration -->
        <div class="header-info-section">
          <div class="version-status-info">
            <div class="current-version-badge">
              <span class="badge bg-primary">Version actuelle: {{ currentVersion }}</span>
            </div>
            <div class="last-update-info">
              <span class="text-muted">Dernière mise à jour: {{ lastUpdateDate }}</span>
            </div>
          </div>
          <div class="admin-button-section">
            <button @click="goToAdmin" class="btn btn-outline-primary admin-btn">
              <i class="fas fa-cog me-2"></i>Administration
            </button>
          </div>
        </div>
      </div>

      <!-- Filtres -->
      <div class="filters mb-4">
        <div class="row">
          <div class="col-md-4">
            <input 
              type="date" 
              v-model="startDate" 
              class="form-control" 
              @change="filterVersions"
              placeholder="Date de début"
            />
          </div>
          <div class="col-md-4">
            <input 
              type="date" 
              v-model="endDate" 
              class="form-control" 
              @change="filterVersions"
              placeholder="Date de fin"
            />
          </div>
          <div class="col-md-4">
            <button @click="resetFilters" class="btn btn-outline-secondary">
              Réinitialiser les filtres
            </button>
          </div>
        </div>
      </div>

      <!-- Liste des versions -->
      <div class="versions-list">
        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Chargement...</span>
          </div>
        </div>

        <div v-else-if="filteredVersions.length === 0" class="alert alert-info">
          Aucune version trouvée.
        </div>

        <div v-else class="version-items">
          <div 
            v-for="version in filteredVersions" 
            :key="version.id" 
            class="version-item card mb-3"
            @click="showVersionDetails(version)"
          >
                         <div class="card-body">
               <div class="version-header">
                 <h5 class="card-title mb-1">
                   {{ version.title }}
                   <span class="badge bg-success ms-2">Publiée</span>
                 </h5>
                 <p class="text-muted mb-2">
                   Version {{ version.version_number }}
                   <br>
                   <span v-if="version.date_publication">
                     • Publiée le {{ formatDate(version.date_publication) }}
                   </span>
                   <span class="badge bg-info ms-2">
                     {{ version.memo_count || 0 }} mémos
                   </span>
                 </p>
               </div>
               
               <p class="card-text mb-3">{{ version.latest_memo || 'Aucun mémo disponible' }}</p>
               
               <div class="version-actions">
                 <button 
                   class="btn btn-outline-primary"
                   @click.stop="showVersionDetails(version)"
                 >
                   🔍 Voir les détails
                 </button>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de détails de version -->
    <b-modal
      v-model="showVersionModal"
      :title="`Version ${selectedVersion?.version_number} - ${selectedVersion?.title}`"
      size="xl"
      hide-footer
    >
      <div v-if="selectedVersion" class="version-details">
        <!-- En-tête de la version -->
        <div class="version-header-details mb-4">
          <h4>{{ selectedVersion.title }}</h4>
          <div class="version-meta">
            <span class="badge bg-primary me-2">Version {{ selectedVersion.version_number }}</span>
            <span class="badge bg-success me-2">Publiée</span>
            <span class="badge bg-info me-2">{{ selectedVersion.memo_count || 0 }} mémos</span>
          </div>
          <p class="text-muted mt-2">
            <span v-if="selectedVersion.date_publication">
              <br>
              • Publiée le {{ formatDate(selectedVersion.date_publication) }}
            </span>
          </p>
        </div>

                 <!-- Liste des mémos -->
         <div class="memos-section">
           <h5>Historique des mémos</h5>
           <div v-if="versionLogs.length === 0" class="text-center py-4">
             <p class="text-muted">Aucun mémo trouvé pour cette version.</p>
           </div>
           <div v-else class="logs-list">
                           <div 
                v-for="log in versionLogs" 
                :key="log.id"
                class="log-item"
              >
               <p class="mb-2">{{ log.memo }}</p>
             </div>
           </div>
         </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import api from '@/plugins/api';

export default {
  name: 'VersionsPage',
  data() {
    return {
      versions: [],
      filteredVersions: [],
      loading: true,
      startDate: '',
      endDate: '',
      showVersionModal: false,
      selectedVersion: null,
      versionLogs: [],
      currentVersion: '1.0.0',
      lastUpdateDate: ''
    };
  },
  
  async mounted() {
    await this.loadVersions();
  },
  
  methods: {
    async loadVersions() {
      try {
        this.loading = true;
        const { data } = await api.get('/versions/');
        this.versions = data;
        this.filteredVersions = [...this.versions];
        
        // Mettre à jour la version actuelle et la date de dernière mise à jour
        if (this.versions.length > 0) {
          const latestVersion = this.versions[0];
          this.currentVersion = latestVersion.version_number;
          this.lastUpdateDate = this.formatDate(latestVersion.release_date);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des versions:', error);
      } finally {
        this.loading = false;
      }
    },

    filterVersions() {
      let filtered = [...this.versions];

      // Filtre par date
      if (this.startDate) {
        filtered = filtered.filter(v => new Date(v.release_date) >= new Date(this.startDate));
      }

      if (this.endDate) {
        filtered = filtered.filter(v => new Date(v.release_date) <= new Date(this.endDate));
      }

      this.filteredVersions = filtered;
    },

    resetFilters() {
      this.startDate = '';
      this.endDate = '';
      this.filteredVersions = [...this.versions];
    },

    async showVersionDetails(version) {
      try {
        this.selectedVersion = version;
        
        // Charger les logs de la version
        const { data } = await api.get(`/versions/${version.id}/logs`);
        this.versionLogs = data;
        
        this.showVersionModal = true;
      } catch (error) {
        console.error('Erreur lors du chargement des détails:', error);
        alert('Erreur lors du chargement des détails');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    goToAdmin() {
      this.$router.push('/admin/versions');
    }
  }
};
</script>

<style scoped>
.versions-page {
  padding-bottom: 2rem;
}

.versions-container {
  max-width: 800px;
  margin: 0 auto;
}

.version-item {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.version-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.version-header h5 {
  color: #333;
  margin-bottom: 0.5rem;
}

.version-details .version-info {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
}

.description-section {
  margin-top: 2rem;
}

.description-section h5 {
  margin-bottom: 1rem;
  color: #495057;
}

.filters {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
}

.version-info .badge {
  font-size: 0.8rem;
}

.header-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.header-title-section {
  text-align: center;
}

.header-title {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
  font-size: 1.75rem;
}

.header-info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.version-status-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.current-version-badge {
  margin-bottom: 0.25rem;
}

.current-version-badge .badge {
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
}

.last-update-info {
  font-size: 0.9rem;
  color: #6c757d;
}

.admin-button-section {
  margin-top: 0.5rem;
}

.admin-btn {
  font-weight: 500;
  padding: 0.5rem 1.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.admin-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Styles responsives */
@media (min-width: 768px) {
  .header-info-section {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  
  .version-status-info {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    text-align: left;
  }
  
  .current-version-badge {
    margin-bottom: 0;
  }
  
  .admin-button-section {
    margin-top: 0;
  }
  
  .header-title {
    font-size: 2rem;
  }
}

@media (max-width: 767px) {
  .header-section {
    padding: 1rem;
  }
  
  .header-title {
    font-size: 1.5rem;
  }
  
  .current-version-badge .badge {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
  
  .last-update-info {
    font-size: 0.85rem;
  }
  
  .admin-btn {
    padding: 0.4rem 1.2rem;
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  .header-section {
    padding: 0.75rem;
  }
  
  .header-title {
    font-size: 1.25rem;
  }
  
  .version-status-info {
    gap: 0.25rem;
  }
  
  .current-version-badge .badge {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
  }
  
  .last-update-info {
    font-size: 0.8rem;
  }
  
  .admin-btn {
    padding: 0.3rem 1rem;
    font-size: 0.85rem;
  }
}
</style>
